import {
  cn,
  generateLinkFromGeneralLink,
  generateTargetFromLink,
  LinkConfig,
} from "@/lib/utils";
import { Button } from "./button";
import Link from "next/link";
import { useLocale } from "next-intl";
import { UploadFile } from "@/types/generated";

export interface CtaButtonProps {
  label: string;
  hyperlink?: LinkConfig;
  target?: string;
  image?: UploadFile;
  pdf?: UploadFile;
  secondaryLabel?: string;
  className?: string;
  icon?: React.ReactNode;
  styleConfig?: {
    bgColor?: string;
    textColor?: string;
    fontSize?: string;
    fontWeight?: string;
    padding?: string;
    margin?: string;
    width?: string;
    borderRadius?: string;
  };
}

const defaultStyles = {
  margin: "0",
  width: "auto",
};

export default function CtaButton({
  label,
  hyperlink,
  pdf,
  styleConfig = {},
  className = "",
  icon,
}: Readonly<CtaButtonProps>) {
  const locale = useLocale();

  // Check if PDF is provided
  const pdfUrl = pdf?.cloudflare_url || pdf?.url;
  const hasPdf = Boolean(pdfUrl);

  const filteredStyleConfig = Object.entries(styleConfig).reduce(
    (acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        acc[key as keyof typeof styleConfig] = value;
      }
      return acc;
    },
    {} as Partial<typeof styleConfig>,
  );

  const mergedStyles = { ...defaultStyles, ...filteredStyleConfig };

  const inlineStyle: React.CSSProperties = {
    backgroundColor: mergedStyles.bgColor,
    color: mergedStyles.textColor,
    fontSize: mergedStyles.fontSize,
    fontWeight: mergedStyles.fontWeight,
    padding: mergedStyles.padding,
    margin: mergedStyles.margin,
    width: mergedStyles.width,
    borderRadius: mergedStyles?.borderRadius,
  };

  const baseClasses = `
    inline-flex 
    items-center 
    justify-center 
    transition-all
    hover:opacity-90
    active:opacity-100
    focus:opacity-100
    focus:outline-none
    active:outline-none
    rounded-none
    max-w-full
    px-[80px]
    py-[18px]
    bg-[#FBC418]
    text-black
    hover:bg-[#FCCB2E]
    active:bg-[#FCCB2E]
    text-base
  `;

  const content = (
    <Button
      className={cn(baseClasses, className)}
      style={
        {
          ...inlineStyle,
          "--current-bg-color": mergedStyles.bgColor || "#FBC418",
        } as React.CSSProperties
      }
    >
      {label}
      {icon && <span className="ml-2">{icon}</span>}
    </Button>
  );

  // Priority: PDF > hyperlink > no link
  if (hasPdf && pdfUrl) {
    return (
      <Link href={pdfUrl} target="_blank" rel="noopener noreferrer">
        {content}
      </Link>
    );
  }

  return hyperlink ? (
    <Link
      href={generateLinkFromGeneralLink(hyperlink, locale)}
      target={generateTargetFromLink(hyperlink)}
      className="focus:outline-none active:outline-none"
    >
      {content}
    </Link>
  ) : (
    content
  );
}
